import React, { useState, useEffect } from 'react';
import './App.css';
import { handleSubmit } from './handleSubmit.js';
import { API_BASE_URL } from './apiConfig';

export function SmartCapFirmware() {
  const [newVersion, setNewVersion] = useState('');
  const [selectedDatFile, setSelectedDatFile] = useState(null);
  const [selectedHexFile, setSelectedHexFile] = useState(null);
  const [latestVersion, setLatestVersion] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [allVersions, setAllVersions] = useState([]);
  const [selectedLatestVersion, setSelectedLatestVersion] = useState('');
  const [versionError, setVersionError] = useState(null);
  const [isSettingVersion, setIsSettingVersion] = useState(false);

  const fetchLatestVersion = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_BASE_URL}/ota/latest-version`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setLatestVersion(data.result || data.data?.result || 'N/A');
    } catch (err) {
      setError(err.message);
      console.error('Error fetching latest version:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAllVersions = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/ota/all-versions`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setAllVersions(data.result || data.data?.result || []);
    } catch (err) {
      console.error('Error fetching all versions:', err);
    }
  };

  useEffect(() => {
    fetchLatestVersion();
    fetchAllVersions();
  }, []);

  const handleUpload = async (e) => {
    try {
      setUploadError(null);
      setIsUploading(true);
      const result = await handleSubmit(e, selectedDatFile, selectedHexFile, newVersion, () => {
        fetchLatestVersion();
        fetchAllVersions();
      });
      
      if (result.success) {
        setSelectedDatFile(null);
        setSelectedHexFile(null);
        setNewVersion('');
      } else {
        setUploadError(result.error);
      }
    } catch (error) {
      console.error('Upload failed:', error);
      setUploadError(error.message);
    } finally {
      setIsUploading(false);
    }
  };

  const handleSetLatestVersion = async () => {
    if (!selectedLatestVersion) {
      setVersionError('Please select a version');
      return;
    }

    try {
      setIsSettingVersion(true);
      setVersionError(null);
      const response = await fetch(`${API_BASE_URL}/ota/set-latest-version?version=${selectedLatestVersion}`, {
        method: 'POST'
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to set latest version');
      }

      // Refresh the latest version display
      await fetchLatestVersion();
      setSelectedLatestVersion('');
    } catch (err) {
      setVersionError(err.message);
      console.error('Error setting latest version:', err);
    } finally {
      setIsSettingVersion(false);
    }
  };

  return (
    <div className="box" style={{ padding: '20px' }}>
      {/* Latest Version Section */}
      <div style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <h3 style={{ margin: 10 }}>Latest Version:</h3>
          {isLoading ? (
            <div className="version-pill">Loading...</div>
          ) : error ? (
            <div className="version-pill error" title={error}>Error</div>
          ) : (
            <div className="version-pill">{latestVersion}</div>
          )}
        </div>
      </div>

      {/* Upload Section */}
      <h3 style={{ marginBottom: '15px', margin: 10 }}>Upload:</h3>
      {uploadError && (
        <div style={{
          color: 'red',
          padding: '10px',
          marginBottom: '15px',
          border: '1px solid red',
          borderRadius: '4px',
          backgroundColor: '#ffeeee'
        }}>
          {uploadError}
        </div>
      )}
      <div className="upload-row">
        <div className="upload-section">
          <h4>.dat file</h4>
          <label htmlFor="file-upload-dat">
            <div className="upload-square">
              {selectedDatFile ? (
                <p>{selectedDatFile.name}</p>
              ) : (
                <>
                  <span>+</span>
                  <p>Upload file</p>
                </>
              )}
            </div>
          </label>
          <input
            id="file-upload-dat"
            type="file"
            accept=".dat"
            style={{ display: 'none' }}
            onChange={(e) => setSelectedDatFile(e.target.files[0])}
          />
        </div>
        <div className="upload-section">
          <h4>.hex file</h4>
          <label htmlFor="file-upload-hex">
            <div className="upload-square">
              {selectedHexFile ? (
                <p>{selectedHexFile.name}</p>
              ) : (
                <>
                  <span>+</span>
                  <p>Upload file</p>
                </>
              )}
            </div>
          </label>
          <input
            id="file-upload-hex"
            type="file"
            accept=".hex"
            style={{ display: 'none' }}
            onChange={(e) => setSelectedHexFile(e.target.files[0])}
          />
        </div>
      </div>
      <div style={{ marginTop: '20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
          <h4 style={{ margin: 20 }}>New Version:</h4>
          <input
            type="text"
            value={newVersion}
            onChange={(e) => setNewVersion(e.target.value)}
            placeholder="Enter version number"
            style={{
              padding: '8px 12px',
              border: '1px solid #ccc',
              borderRadius: '4px',
              flex: 1,
              maxWidth: '200px'
            }}
          />
        </div>
      </div>
      <button
        className="upload-button"
        style={{ marginTop: '20px' }}
        onClick={handleUpload}
        disabled={isUploading || !selectedDatFile || !selectedHexFile || !newVersion}
      >
        {isUploading ? 'Uploading...' : 'Upload'}
      </button>

      {/* Set Latest Version Section */}
      <div style={{ marginTop: '40px', paddingTop: '20px', borderTop: '1px solid #eee' }}>
        <h3 style={{ marginBottom: '15px', margin: 10 }}>Set Latest Version:</h3>
        {versionError && (
          <div style={{
            color: 'red',
            padding: '10px',
            marginBottom: '15px',
            border: '1px solid red',
            borderRadius: '4px',
            backgroundColor: '#ffeeee'
          }}>
            {versionError}
          </div>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: '20px', flexWrap: 'wrap' }}>
          <select
            value={selectedLatestVersion}
            onChange={(e) => setSelectedLatestVersion(e.target.value)}
            style={{
              padding: '8px 12px',
              border: '1px solid #ccc',
              borderRadius: '4px',
              minWidth: '200px'
            }}
          >
            <option value="">Select a version</option>
            {allVersions.map((version) => (
              <option key={version} value={version}>
                {version}
              </option>
            ))}
          </select>
          <button
            className="upload-button"
            onClick={handleSetLatestVersion}
            disabled={isSettingVersion || !selectedLatestVersion}
          >
            {isSettingVersion ? 'Setting...' : 'Set as Latest'}
          </button>
        </div>
      </div>
    </div>
  );
}